// src/pages/DataTeam/DataTeamDownloadPerStudent.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TitleWithBack from '../../components/common/TitleWithBack';
import { Container, Row, Col, Form, Button, Badge } from 'react-bootstrap';
import { FaSearch, FaDownload } from 'react-icons/fa';
import { saveAs } from 'file-saver';
import PaginatedTable from '../../components/PaginatedTable';
import { useDialog } from '../../context/DialogContext';
import { isSeriesActive } from '../../utils/helpers';

export default function DataTeamDownloadPerStudent() {
  const { alert } = useDialog();
  // --- Series filter state ---
  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const selectedSeries = React.useMemo(() => seriesList.find(s => String(s.id) === String(selectedSeriesId)), [seriesList, selectedSeriesId]);
  const activeSeriesList = React.useMemo(() => seriesList.filter(series => isSeriesActive(series)), [seriesList]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  useEffect(() => {
    setSeriesLoading(true);
    axios.get('/api/exam-series')
      .then(res => {
        const allSeries = Array.isArray(res.data) ? res.data : [];
        setSeriesList(allSeries);
        setSeriesLoading(false);
      })
      .catch(() => {
        setError('Error fetching exam series.');
        setSeriesLoading(false);
      });
  }, []);

  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({
    studentNumber: '',
    studentName: '',
    centreNumber: '',
    moduleCode: '',
    module: '',
    year: '',         
    semester: '',     
    examSeries: ''    
  });
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [studentWork, setStudentWork] = useState({});

  useEffect(() => {
    setLoading(true);
    axios
      .get('/api/students')
      .then((res) => {
        setStudents(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching students.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (students.length === 0) return;
    axios
      .get('/api/archiver/reports/students-work')
      .then((res) => {
        const workByStudent = {};
        (res.data || []).forEach((w) => {
          if (w.studentId) {
            if (!workByStudent[w.studentId]) workByStudent[w.studentId] = [];
            workByStudent[w.studentId].push(w);
          }
        });
        setStudentWork(workByStudent);
        console.log('Loaded studentWork:', workByStudent);
      })
      .catch(() => {
        console.log('No student work data available yet');
      });
  }, [students]);

  const studentIdsWithWorkInSeries = React.useMemo(() => {
    if (!studentWork) return new Set();
    const ids = new Set();
    Object.entries(studentWork).forEach(([sid, workList]) => {
      if (!selectedSeriesId) {
        if (workList.some(w => activeSeriesList.some(series => String(series.name) === String(w.examSeries)))) {
          ids.add(Number(sid));
        }
      } else {
        if (workList.some(w => String(w.examSeries) === String(selectedSeries?.name))) {
          ids.add(Number(sid));
        }
      }
    });
    console.log('studentIdsWithWorkInSeries:', ids, 'for series', selectedSeriesId ? selectedSeries?.name : '(all active)', selectedSeriesId);
    return ids;
  }, [selectedSeriesId, selectedSeries, studentWork, activeSeriesList]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const filteredData = students.filter((s) => {
    if (!studentIdsWithWorkInSeries.has(s.id)) {
      return false;
    }
    return true;
  });

  const flatRows = [];
  filteredData.forEach(student => {
    const works = (studentWork[student.id] || []).filter(w => {
      if (!selectedSeriesId) {
        return activeSeriesList.some(series => String(series.name) === String(w.examSeries));
      } else {
        return String(w.examSeries) === String(selectedSeries?.name);
      }
    });
    works.forEach(work => flatRows.push({ student, work }));
  });

  const filteredFlatRows = flatRows.filter(row => {
    const { student: s, work } = row;
    if (filters.studentNumber && !String(s.registrationNumber).toLowerCase().includes(filters.studentNumber.toLowerCase())) {
      return false;
    }
    if (filters.studentName && !s.name.toLowerCase().includes(filters.studentName.toLowerCase())) {
      return false;
    }
    if (filters.centreNumber && !String(s.examCentreId || '').toLowerCase().includes(filters.centreNumber.toLowerCase())) {
      return false;
    }
    if (filters.moduleName && work.module) {
      if (!work.module.toLowerCase().includes(filters.moduleName.toLowerCase())) {
        return false;
      }
    } else if (filters.moduleName) {
      return false;
    }
    if (filters.moduleCode && work.moduleCode) {
      if (!work.moduleCode.toLowerCase().includes(filters.moduleCode.toLowerCase())) {
        return false;
      }
    } else if (filters.moduleCode) {
      return false;
    }
    //  Year filter
    if (filters.year && s.yearOfStudy?.name) {
      if (!s.yearOfStudy.name.toLowerCase().includes(filters.year.toLowerCase())) {
        return false;
      }
    } else if (filters.year) {
      return false;
    }
    //  Semester filter
    if (filters.semester && s.semester?.name) {
      if (!s.semester.name.toLowerCase().includes(filters.semester.toLowerCase())) {
        return false;
      }
    } else if (filters.semester) {
      return false;
    }
    // Exam Series filter
    if (filters.examSeries && work.examSeries) {
      if (!work.examSeries.toLowerCase().includes(filters.examSeries.toLowerCase())) {
        return false;
      }
    } else if (filters.examSeries) {
      return false;
    }
   
    return true;
  });

  const sortedData = [...filteredFlatRows].sort((a, b) => {
    if (!sortColumn) return 0;
    let valA = '', valB = '';
    if (sortColumn === 'id') {
      valA = String(a.student.id || '');
      valB = String(b.student.id || '');
    } else if (sortColumn === 'name') {
      valA = a.student.name.toLowerCase();
      valB = b.student.name.toLowerCase();
    } else if (sortColumn === 'moduleName') {
      valA = (a.work.module || '') ? a.work.module.toLowerCase() : '';
      valB = (b.work.module || '') ? b.work.module.toLowerCase() : '';
    } else if (sortColumn === 'moduleCode') {
      valA = (a.work.moduleCode || '') ? a.work.moduleCode.toLowerCase() : '';
      valB = (b.work.moduleCode || '') ? b.work.moduleCode.toLowerCase() : '';
    } else if (sortColumn === 'year') {
      valA = (a.student.yearOfStudy && a.student.yearOfStudy.name) ? a.student.yearOfStudy.name.toLowerCase() : '';
      valB = (b.student.yearOfStudy && b.student.yearOfStudy.name) ? b.student.yearOfStudy.name.toLowerCase() : '';
    } else if (sortColumn === 'semester') {
      valA = (a.student.semester && a.student.semester.name) ? a.student.semester.name.toLowerCase() : '';
      valB = (b.student.semester && b.student.semester.name) ? b.student.semester.name.toLowerCase() : '';
    }
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortOrder('asc');
    }
  };

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'id', header: <span onClick={() => handleSort('id')} style={{cursor: 'pointer'}}>Reg no. {sortColumn === 'id' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'name', header: <span onClick={() => handleSort('name')} style={{cursor: 'pointer'}}>Student name {sortColumn === 'name' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'moduleName', header: <span onClick={() => handleSort('moduleName')} style={{cursor: 'pointer'}}>Module name {sortColumn === 'moduleName' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'moduleCode', header: <span onClick={() => handleSort('moduleCode')} style={{cursor: 'pointer'}}>Module Code {sortColumn === 'moduleCode' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'year', header: <span onClick={() => handleSort('year')} style={{cursor: 'pointer'}}>Year {sortColumn === 'year' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'semester', header: <span onClick={() => handleSort('semester')} style={{cursor: 'pointer'}}>Semester {sortColumn === 'semester' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'examSeries', header: <span onClick={() => handleSort('examSeries')} style={{cursor: 'pointer'}}>Exam Series {sortColumn === 'examSeries' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'uploadedWork', header: 'Uploaded Work' },
    { key: 'download', header: 'Download' }
  ];

  const renderRow = (row, idx, pageIndex, pageSize) => {
    const { student: s, work } = row;
    return (
      <>
        <td>{(pageIndex * pageSize) + idx + 1}</td>
        <td><small>{s.registrationNumber}</small></td>
        <td><small>{s.name}</small></td>
        <td><small>{work.module || ''}</small></td>
        <td><small>{work.moduleCode || ''}</small></td>
        <td><small>{s.yearOfStudy ? s.yearOfStudy.name : ''}</small></td>
        <td><small>{s.semester ? s.semester.name : ''}</small></td>
        <td><small>{work.examSeries || ''}</small></td>
        <td>
          <Badge bg="info" className="me-1">
            {work.module || 'Work'}
          </Badge>
        </td>
        <td>
          <Button 
            variant="outline-primary" 
            onClick={() => handleDownloadSingleWork(s, work)}
            disabled={!work}
          >
            <FaDownload className="me-1" />
            <small>Download</small> 
          </Button>
        </td>
      </>
    );
  };

  const handleDownloadSingleWork = async (student, work) => {
    if (!work) return;
    try {
      const res = await axios.get(`/api/archiver/reports/students-work/download/${work.documentId}`, { responseType: 'blob' });
      const sanitize = str => (str || '').replace(/[^a-zA-Z0-9]/g, '');
      const getExtension = filename => {
        const match = filename ? filename.match(/\.([a-zA-Z0-9]+)$/) : null;
        return match ? '.' + match[1] : '';
      };
      const ext = getExtension(work.documentName);
      const fileName = `${sanitize(student.name)}_${sanitize(student.registrationNumber)}_${sanitize(work.module)}_${sanitize(work.examSeries)}${ext}`;
      saveAs(res.data, fileName);
    } catch (e) {
      console.error('Failed to download file:', e);
      alert('Failed to download this file.', { title: 'Error', variant: 'danger' });
    }
  };

  if (loading) return <Container className="mt-4">Loading...</Container>;
  if (error) return <Container className="mt-4 text-danger">{error}</Container>;

  return (
    <Container fluid className="mt-2">
      <TitleWithBack title="CAD practical work - browse per candidate" />

      {/* Exam Series Selector */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Label>Exam Series</Form.Label>
          <Form.Select
            value={selectedSeriesId}
            onChange={e => setSelectedSeriesId(e.target.value)}
            disabled={seriesLoading || seriesList.length === 0}
          >
            <option value=''>All Active Series</option>
            {seriesList.map(series => (
              <option key={series.id} value={series.id}>
                {series.name} ({series.startDate} - {series.endDate})
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Search bars */}
      <Row className="mb-3">
        <Col md={1}>
          <Form.Label>Reg no.</Form.Label>
          <Form.Control
            type="text"
            name="studentNumber"
            value={filters.studentNumber}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            name="studentName"
            value={filters.studentName}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Module Name</Form.Label>
          <Form.Control
            type="text"
            name="moduleName"
            value={filters.moduleName}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Module code</Form.Label>
          <Form.Control
            type="text"
            name="moduleCode"
            value={filters.moduleCode}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Year</Form.Label>
          <Form.Control
            type="text"
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={1}>
          <Form.Label>Semester</Form.Label>
          <Form.Control
            type="text"
            name="semester"
            value={filters.semester}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Exam Series</Form.Label>
          <Form.Control
            type="text"
            name="examSeries"
            value={filters.examSeries}
            onChange={handleFilterChange}
          />
        </Col>
      </Row>

      {sortedData.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={sortedData}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
    </Container>
  );
}
